import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useBeforeUnload, useNavigate } from 'react-router-dom';
import { useAdminLocationGroups, useAdminReferenceOptions, useAdminSquads, } from '@/api/opengater/useLocations';
import { Button } from '@/components/primitives/Button';
import { Input } from '@/components/primitives/Input';
import { SegmentedControl } from '@/components/primitives/SegmentedControl';
import { Icon } from '@/components/Icon';
import { SidePanel } from '@/components/SidePanel';
import { ListRow, SettingsGroup, SettingsLoading, SettingsNote, SettingsState, } from '@/components/settings/Settings';
import { DirtyNavigationGuard } from './DirtyNavigationGuard';
import { ReferenceOptionForm } from './ReferenceOptionForm';
import { LocationGroupForm } from './LocationGroupForm';
import { SquadForm } from './SquadForm';
import s from '../OpengaterLocations.module.css';
export function LocationDirectories({ view, onNavigate, onClose, }) {
    const options = useAdminReferenceOptions();
    const squads = useAdminSquads('all');
    const groups = useAdminLocationGroups();
    const navigate = useNavigate();
    const [dirty, setDirty] = useState(false);
    const [groupSearch, setGroupSearch] = useState('');
    const [notice, setNotice] = useState(null);
    const [pendingNavigation, setPendingNavigation] = useState(null);
    const requestNavigation = useCallback((action) => {
        if (dirty)
            setPendingNavigation(() => action);
        else
            action();
    }, [dirty]);
    useBeforeUnload(useCallback((event) => {
        if (!dirty)
            return;
        event.preventDefault();
        event.returnValue = '';
    }, [dirty]));
    useEffect(() => {
        if (!dirty)
            return undefined;
        const guardLinkNavigation = (event) => {
            if (event.defaultPrevented ||
                event.button !== 0 ||
                event.metaKey ||
                event.ctrlKey ||
                event.shiftKey ||
                event.altKey) {
                return;
            }
            const anchor = event.target instanceof Element ? event.target.closest('a[href]') : null;
            if (!(anchor instanceof HTMLAnchorElement) ||
                anchor.download ||
                (anchor.target && anchor.target !== '_self')) {
                return;
            }
            const target = new URL(anchor.href, window.location.href);
            if (target.origin !== window.location.origin)
                return;
            const href = `${target.pathname}${target.search}${target.hash}`;
            const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
            if (href === current)
                return;
            event.preventDefault();
            event.stopPropagation();
            requestNavigation(() => navigate(href));
        };
        document.addEventListener('click', guardLinkNavigation, true);
        return () => document.removeEventListener('click', guardLinkNavigation, true);
    }, [dirty, navigate, requestNavigation]);
    const confirmDiscard = () => {
        const action = pendingNavigation;
        setPendingNavigation(null);
        setDirty(false);
        action?.();
    };
    const directory = view.directory ?? 'options';
    const nested = view.groupId != null ||
        view.referenceOptionId != null ||
        view.squadId != null ||
        view.create === 'group' ||
        view.create === 'referenceOption' ||
        view.create === 'squad';
    const filteredGroups = useMemo(() => {
        const query = groupSearch.trim().toLocaleLowerCase('ru-RU');
        if (!query)
            return groups.data ?? [];
        return (groups.data ?? []).filter((group) => group.name.toLocaleLowerCase('ru-RU').includes(query));
    }, [groupSearch, groups.data]);
    const back = () => requestNavigation(() => {
        if (view.create != null) {
            onNavigate({ create: null }, { replace: true });
        }
        else {
            onNavigate({ groupId: null, referenceOptionId: null, squadId: null });
        }
    });
    return (_jsx(SidePanel, { title: "\u0421\u043F\u0440\u0430\u0432\u043E\u0447\u043D\u0438\u043A\u0438", onClose: () => requestNavigation(onClose), children: _jsxs("div", { className: s.panelContent, children: [_jsx(DirtyNavigationGuard, { open: pendingNavigation != null, onCancel: () => setPendingNavigation(null), onConfirm: confirmDiscard }), notice && _jsx(SettingsNote, { tone: "ok", children: notice }), nested && (_jsx("div", { className: s.stackHeader, children: _jsxs(Button, { type: "button", variant: "ghost", onClick: back, children: [_jsx(Icon, { name: "chev-right", size: 14 }), " \u041D\u0430\u0437\u0430\u0434 \u043A \u0441\u043F\u0440\u0430\u0432\u043E\u0447\u043D\u0438\u043A\u0443"] }) })), view.groupId != null || view.create === 'group' ? (_jsx(LocationGroupForm, { groupId: view.groupId, creating: view.create === 'group', onDirtyChange: setDirty, onCreated: (groupId) => {
                        setDirty(false);
                        if (groupId == null) {
                            setNotice('Группа создана. Откройте её из обновлённого списка.');
                            onNavigate({ create: null }, { replace: true });
                        }
                        else {
                            setNotice(null);
                            onNavigate({ create: null, groupId }, { replace: true });
                        }
                    }, onDone: () => {
                        setDirty(false);
                        if (view.create === 'group')
                            onNavigate({ create: null }, { replace: true });
                        else
                            onNavigate({ groupId: null });
                    } })) : view.referenceOptionId != null || view.create === 'referenceOption' ? (_jsx(ReferenceOptionForm, { optionId: view.referenceOptionId, creating: view.create === 'referenceOption', onDirtyChange: setDirty, onCreated: (referenceOptionId) => {
                        setDirty(false);
                        if (referenceOptionId == null) {
                            setNotice('Тип опции создан. Откройте его из обновлённого списка.');
                            onNavigate({ create: null }, { replace: true });
                        }
                        else {
                            setNotice(null);
                            onNavigate({ create: null, referenceOptionId }, { replace: true });
                        }
                    }, onDeleted: () => {
                        setDirty(false);
                        if (view.create === 'referenceOption') {
                            onNavigate({ create: null }, { replace: true });
                        }
                        else
                            back();
                    } })) : view.squadId != null || view.create === 'squad' ? (_jsx(SquadForm, { squadId: view.squadId, creating: view.create === 'squad', onDirtyChange: setDirty, onCreated: (id) => {
                        setDirty(false);
                        if (id == null) {
                            setNotice('Сквад создан. Откройте его из обновлённого списка.');
                            onNavigate({ create: null }, { replace: true });
                        }
                        else {
                            setNotice(null);
                            onNavigate({ create: null, squadId: id }, { replace: true });
                        }
                    }, onDone: () => {
                        setDirty(false);
                        if (view.create === 'squad')
                            onNavigate({ create: null }, { replace: true });
                        else
                            back();
                    } })) : (_jsxs(_Fragment, { children: [_jsx("div", { className: s.panelSegments, children: _jsx(SegmentedControl, { value: directory, onChange: (next) => requestNavigation(() => onNavigate({ directory: next })), options: [
                                    { value: 'groups', label: 'Группы' },
                                    { value: 'options', label: 'Типы опций' },
                                    { value: 'squads', label: 'Сквады' },
                                ] }) }), directory === 'groups' ? (_jsxs(_Fragment, { children: [_jsxs("div", { className: s.directoryToolbar, children: [_jsx(Input, { "aria-label": "\u041F\u043E\u0438\u0441\u043A \u0433\u0440\u0443\u043F\u043F", placeholder: "\u041D\u0430\u0439\u0442\u0438 \u0433\u0440\u0443\u043F\u043F\u0443", value: groupSearch, onChange: (event) => setGroupSearch(event.target.value) }), _jsxs(Button, { type: "button", onClick: () => {
                                                setNotice(null);
                                                onNavigate({ create: 'group' });
                                            }, children: [_jsx(Icon, { name: "plus", size: 15 }), " \u041D\u043E\u0432\u0430\u044F \u0433\u0440\u0443\u043F\u043F\u0430"] })] }), groups.isLoading ? (_jsx(SettingsLoading, {})) : groups.error ? (_jsx(SettingsNote, { tone: "error", children: "\u041D\u0435 \u0443\u0434\u0430\u043B\u043E\u0441\u044C \u0437\u0430\u0433\u0440\u0443\u0437\u0438\u0442\u044C \u0433\u0440\u0443\u043F\u043F\u044B." })) : filteredGroups.length ? (_jsx(SettingsGroup, { title: "\u0412\u0441\u0435 \u0433\u0440\u0443\u043F\u043F\u044B", children: filteredGroups.map((group) => (_jsx(ListRow, { title: group.name, sub: `${group.translations.length} переводов`, onOpen: () => onNavigate({ groupId: group.id }) }, group.id))) })) : (_jsx(SettingsState, { children: groupSearch.trim() ? 'Группы не найдены' : 'Групп пока нет' }))] })) : directory === 'options' ? (_jsxs(_Fragment, { children: [_jsx("div", { className: s.directoryActions, children: _jsxs(Button, { type: "button", onClick: () => {
                                            setNotice(null);
                                            onNavigate({ create: 'referenceOption' });
                                        }, children: [_jsx(Icon, { name: "plus", size: 15 }), " \u041D\u043E\u0432\u044B\u0439 \u0442\u0438\u043F \u043E\u043F\u0446\u0438\u0438"] }) }), options.isLoading ? (_jsx(SettingsLoading, {})) : options.error ? (_jsx(SettingsNote, { tone: "error", children: "\u041D\u0435 \u0443\u0434\u0430\u043B\u043E\u0441\u044C \u0437\u0430\u0433\u0440\u0443\u0437\u0438\u0442\u044C \u0442\u0438\u043F\u044B \u043E\u043F\u0446\u0438\u0439." })) : options.data?.length ? (_jsx(SettingsGroup, { title: "\u0412\u0441\u0435 \u0442\u0438\u043F\u044B \u043E\u043F\u0446\u0438\u0439", children: options.data.map((option) => (_jsx(ListRow, { title: option.name, sub: [option.has_squads ? 'Со сквадами' : 'Без сквадов', option.description]
                                            .filter(Boolean)
                                            .join(' · '), onOpen: () => onNavigate({ referenceOptionId: option.id }) }, option.id))) })) : (_jsx(SettingsState, { children: "\u0422\u0438\u043F\u043E\u0432 \u043E\u043F\u0446\u0438\u0439 \u043F\u043E\u043A\u0430 \u043D\u0435\u0442" }))] })) : (_jsxs(_Fragment, { children: [_jsx("div", { className: s.directoryActions, children: _jsxs(Button, { type: "button", onClick: () => {
                                            setNotice(null);
                                            onNavigate({ create: 'squad' });
                                        }, children: [_jsx(Icon, { name: "plus", size: 15 }), " \u041D\u043E\u0432\u044B\u0439 \u0441\u043A\u0432\u0430\u0434"] }) }), squads.isLoading ? (_jsx(SettingsLoading, {})) : squads.error ? (_jsx(SettingsNote, { tone: "error", children: "\u041D\u0435 \u0443\u0434\u0430\u043B\u043E\u0441\u044C \u0437\u0430\u0433\u0440\u0443\u0437\u0438\u0442\u044C \u0441\u043A\u0432\u0430\u0434\u044B." })) : squads.data?.length ? (_jsx(SettingsGroup, { title: "\u0412\u0441\u0435 \u0441\u043A\u0432\u0430\u0434\u044B", children: squads.data.map((squad) => (_jsx(ListRow, { title: squad.name, sub: !squad.is_active ? 'Скрыт' : squad.description || undefined, dim: !squad.is_active, onOpen: () => onNavigate({ squadId: squad.id }) }, squad.id))) })) : (_jsx(SettingsState, { children: "\u0421\u043A\u0432\u0430\u0434\u043E\u0432 \u043F\u043E\u043A\u0430 \u043D\u0435\u0442" }))] }))] }))] }) }));
}
